import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BookOpenCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  Copy,
  FileText,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { useLocation } from "wouter";
import AdminLayout from "../../components/admin/AdminLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import {
  copyConditionInstruction,
  deleteConditionInstruction,
  getAllConditionInstructions,
  updateConditionInstructionLectureOrder,
  updateConditionInstructionReviewStatus,
} from "../../lib/admin-api";
import {
  buildLectureNumberMap,
  COURSE_CATEGORIES,
  sortLectureItems,
} from "../../lib/course-categories";

const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
  cyan: "#0E7490",
  cyanSoft: "#ECFEFF",
  green: "#15803D",
  greenSoft: "#DCFCE7",
  amber: "#B45309",
  amberSoft: "#FEF3C7",
  rose: "#BE123C",
  roseSoft: "#FFF1F2",
};

type ReviewStatus = "draft" | "in_review" | "approved";
type GuidanceLevel = "detailed" | "viewpoints" | "purpose_only";

interface CourseInstruction {
  id: number;
  condition_id?: number;
  review_status?: ReviewStatus;
  updated?: string;
  resolved?: {
    summary?: string;
  };
  instruction_data?: {
    course_category_id?: number | string;
    lecture_order?: number;
    lecture_title?: string;
    selected_guidance_level?: GuidanceLevel;
    summary?: string;
    source_instruction_id?: number;
    superseded_by_instruction_id?: number;
  };
}

const GUIDANCE_LABELS: Record<GuidanceLevel, string> = {
  detailed: "高支援",
  viewpoints: "中支援",
  purpose_only: "低支援",
};

function instructionCourseId(instruction: CourseInstruction): number | null {
  const stored = Number(instruction.instruction_data?.course_category_id);
  if (
    Number.isInteger(stored) &&
    stored >= 1 &&
    stored <= COURSE_CATEGORIES.length
  ) {
    return stored;
  }
  return null;
}

function sortManagedInstructions(
  instructions: CourseInstruction[],
  courseCategoryId: number,
): CourseInstruction[] {
  const approved = sortLectureItems(
    instructions
      .filter((instruction) => instruction.review_status === "approved")
      .map((instruction) => ({
        instruction,
        id: instruction.id,
        courseCategoryId,
        lectureOrder: instruction.instruction_data?.lecture_order ?? null,
      })),
  ).map((item) => item.instruction);
  const pending = instructions
    .filter((instruction) => instruction.review_status !== "approved")
    .sort((left, right) => left.id - right.id);
  return [...approved, ...pending];
}

export default function CourseScenarioSetup() {
  const [location, setLocation] = useLocation();
  const [expandedCourses, setExpandedCourses] = useState<Set<number>>(
    new Set(),
  );
  const [deleteTarget, setDeleteTarget] = useState<CourseInstruction | null>(
    null,
  );
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const instructionsQuery = useQuery({
    queryKey: ["admin", "all-condition-instructions"],
    queryFn: () => getAllConditionInstructions(),
  });
  const updateInstructionStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ReviewStatus }) =>
      updateConditionInstructionReviewStatus(id, status),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "all-condition-instructions"],
      });
      toast({
        title:
          variables.status === "approved"
            ? `課題文 No.${variables.id} を承認しました`
            : `課題文 No.${variables.id} を確認中に戻しました`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "承認状態を変更できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const deleteInstructionMutation = useMutation({
    mutationFn: (id: number) => deleteConditionInstruction(id),
    onSuccess: async (_data, id) => {
      setDeleteTarget(null);
      await queryClient.invalidateQueries({
        queryKey: ["admin", "all-condition-instructions"],
      });
      toast({ title: `課題文 No.${id} を削除しました` });
    },
    onError: (error: Error) => {
      toast({
        title: "課題文を削除できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const copyInstructionMutation = useMutation({
    mutationFn: (instruction: CourseInstruction) =>
      copyConditionInstruction(instruction.id),
    onSuccess: async (data, source) => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "all-condition-instructions"],
      });
      const copied = data?.instruction as CourseInstruction | undefined;
      if (!copied?.id) return;
      const categoryId = instructionCourseId(source);
      toast({ title: "講座の編集コピーを作成しました" });
      setLocation(
        `/teacher/instruction-builder?courseCategory=${categoryId ?? ""}&conditionId=${copied.condition_id ?? source.condition_id ?? ""}&instructionId=${copied.id}`,
      );
    },
    onError: (error: Error) => {
      toast({
        title: "編集コピーを作成できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const reorderLecturesMutation = useMutation({
    mutationFn: ({
      courseCategoryId,
      orderedInstructionIds,
    }: {
      courseCategoryId: number;
      orderedInstructionIds: number[];
    }) =>
      updateConditionInstructionLectureOrder(
        courseCategoryId,
        orderedInstructionIds,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["admin", "all-condition-instructions"],
        }),
        queryClient.invalidateQueries({ queryKey: ["/api/scenarios"] }),
      ]);
      toast({ title: "講座番号を入れ替えました" });
    },
    onError: (error: Error) => {
      toast({
        title: "講座番号を入れ替えられませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const instructions = useMemo(
    () => (instructionsQuery.data?.instructions ?? []) as CourseInstruction[],
    [instructionsQuery.data],
  );
  const statusByCourse = useMemo(
    () =>
      new Map(
        COURSE_CATEGORIES.map((category) => {
          const matched = instructions.filter(
            (instruction) => instructionCourseId(instruction) === category.id,
          );
          return [
            category.id,
            {
              approved: matched.filter(
                (instruction) => instruction.review_status === "approved",
              ).length,
              pending: matched.filter(
                (instruction) => instruction.review_status !== "approved",
              ).length,
            },
          ];
        }),
      ),
    [instructions],
  );
  const instructionsByCourse = useMemo(
    () =>
      new Map(
        COURSE_CATEGORIES.map((category) => [
          category.id,
          sortManagedInstructions(
            instructions.filter(
              (instruction) =>
                instructionCourseId(instruction) === category.id,
            ),
            category.id,
          ),
        ]),
      ),
    [instructions],
  );
  const approvedLectureInstructions = useMemo(
    () =>
      sortLectureItems(
        instructions
          .filter((instruction) => instruction.review_status === "approved")
          .flatMap((instruction) => {
            const courseCategoryId = instructionCourseId(instruction);
            return courseCategoryId === null
              ? []
              : [
                  {
                    instruction,
                    id: instruction.id,
                    courseCategoryId,
                    lectureOrder:
                      instruction.instruction_data?.lecture_order ?? null,
                  },
                ];
          }),
      ),
    [instructions],
  );
  const lectureNumberByInstructionId = useMemo(
    () =>
      buildLectureNumberMap(
        approvedLectureInstructions.map((item) => ({
          id: item.id,
          courseCategoryId: item.courseCategoryId,
          lectureOrder: item.lectureOrder,
        })),
      ),
    [approvedLectureInstructions],
  );
  const isTeacher = location.startsWith("/teacher");
  const completedCount = COURSE_CATEGORIES.filter(
    (category) => (statusByCourse.get(category.id)?.approved ?? 0) > 0,
  ).length;

  const toggleCourseInstructions = (courseCategoryId: number) => {
    setExpandedCourses((current) => {
      const next = new Set(current);
      if (next.has(courseCategoryId)) next.delete(courseCategoryId);
      else next.add(courseCategoryId);
      return next;
    });
  };

  const moveLecture = (
    instructionId: number,
    courseCategoryId: number,
    direction: -1 | 1,
  ) => {
    const categoryLectures = approvedLectureInstructions.filter(
      (item) => item.courseCategoryId === courseCategoryId,
    );
    const currentIndex = categoryLectures.findIndex(
      (item) => item.id === instructionId,
    );
    const targetIndex = currentIndex + direction;
    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= categoryLectures.length ||
      reorderLecturesMutation.isPending
    ) {
      return;
    }
    const nextIds = categoryLectures.map((item) => item.id);
    [nextIds[currentIndex], nextIds[targetIndex]] = [
      nextIds[targetIndex],
      nextIds[currentIndex],
    ];
    reorderLecturesMutation.mutate({
      courseCategoryId,
      orderedInstructionIds: nextIds,
    });
  };

  return (
    <AdminLayout>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          paddingBottom: 32,
          color: COLORS.ink,
          fontFamily:
            '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif',
        }}
      >
        <header
          style={{
            paddingBottom: 20,
            borderBottom: `1px solid ${COLORS.line}`,
          }}
        >
          <div
            style={{
              color: COLORS.muted,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.15em",
            }}
          >
            LECTURE SETTINGS
          </div>
          <h1
            style={{
              margin: "8px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 26,
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            <BookOpenCheck size={28} />
            講座で使う課題文を準備
          </h1>
          <p
            style={{
              margin: "10px 0 0",
              color: COLORS.muted,
              fontSize: 13.5,
              lineHeight: 1.75,
            }}
          >
            学生の「講座を選ぶ」に表示する12カテゴリを、上から順に準備します。承認した課題文には、カテゴリごとに講座1から連番が付きます。
          </p>
        </header>

        <section
          style={{
            marginTop: 14,
            padding: 20,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            background: COLORS.surface,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                課題文の準備状況
              </div>
              <div
                style={{
                  marginTop: 4,
                  color: COLORS.muted,
                  fontSize: 11.5,
                }}
              >
                承認済みの課題文が1件以上あれば設定済みです
              </div>
            </div>
            <div
              style={{
                color: COLORS.cyan,
                fontSize: 13,
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {completedCount} / {COURSE_CATEGORIES.length} カテゴリ
            </div>
          </div>
          <div
            aria-hidden="true"
            style={{
              height: 6,
              marginTop: 12,
              overflow: "hidden",
              borderRadius: 999,
              background: COLORS.line,
            }}
          >
            <div
              style={{
                width: `${(completedCount / COURSE_CATEGORIES.length) * 100}%`,
                height: "100%",
                borderRadius: 999,
                background: COLORS.cyan,
              }}
            />
          </div>
        </section>

        {instructionsQuery.isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 48,
              color: COLORS.muted,
            }}
          >
            <Loader2 className="animate-spin" size={26} />
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: 14,
              marginTop: 14,
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            }}
          >
            {COURSE_CATEGORIES.map((category) => {
              const status = statusByCourse.get(category.id) ?? {
                approved: 0,
                pending: 0,
              };
              const isReady = status.approved > 0;
              const courseInstructions =
                instructionsByCourse.get(category.id) ?? [];
              const isExpanded = expandedCourses.has(category.id);
              return (
                <article
                  key={category.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: 20,
                    border: `1px solid ${category.id === 1 ? COLORS.cyan : COLORS.line}`,
                    borderRadius: 12,
                    background:
                      category.id === 1 ? COLORS.cyanSoft : COLORS.surface,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: COLORS.muted,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.15em",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        CATEGORY {String(category.id).padStart(2, "0")}
                      </div>
                      <h2
                        style={{
                          margin: "7px 0 0",
                          fontSize: 14,
                          fontWeight: 800,
                          lineHeight: 1.5,
                        }}
                      >
                        {category.id}．{category.name}
                      </h2>
                    </div>
                    <span
                      style={{
                        flex: "0 0 auto",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "5px 10px",
                        borderRadius: 999,
                        background: isReady ? COLORS.greenSoft : COLORS.surface,
                        color: isReady ? COLORS.green : COLORS.muted,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {isReady ? (
                        <CheckCircle2 size={14} />
                      ) : (
                        <Clock3 size={14} />
                      )}
                      {isReady ? "設定済み" : "未設定"}
                    </span>
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      color: COLORS.muted,
                      fontSize: 11.5,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    承認済み {status.approved}件
                    {status.pending > 0 ? `・確認中 ${status.pending}件` : ""}
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleCourseInstructions(category.id)}
                    aria-expanded={isExpanded}
                    style={{
                      width: "100%",
                      minHeight: 38,
                      marginTop: 14,
                      padding: "6px 12px",
                      border: `1px solid ${COLORS.line}`,
                      borderRadius: 8,
                      background: COLORS.surface,
                      color: COLORS.ink,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      fontFamily: "inherit",
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                      <FileText size={15} />
                      このカテゴリの課題文を管理
                      <span style={{ color: COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
                        {courseInstructions.length}件
                      </span>
                    </span>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {isExpanded && (
                    <div
                      style={{
                        marginTop: 8,
                        borderTop: `1px solid ${COLORS.line}`,
                      }}
                    >
                      {courseInstructions.length === 0 ? (
                        <div
                          style={{
                            padding: "16px 4px 4px",
                            color: COLORS.muted,
                            fontSize: 11.5,
                            lineHeight: 1.6,
                          }}
                        >
                          このカテゴリに紐づく課題文はまだありません。
                        </div>
                      ) : (
                        courseInstructions.map((instruction) => {
                          const categoryLectures =
                            approvedLectureInstructions.filter(
                              (item) =>
                                item.courseCategoryId === category.id,
                            );
                          const lectureIndex =
                            categoryLectures.findIndex(
                              (item) => item.id === instruction.id,
                            );
                          return (
                            <InstructionManagementRow
                              key={instruction.id}
                              instruction={instruction}
                              lectureNumber={lectureNumberByInstructionId.get(
                                instruction.id,
                              )}
                              isTeacher={isTeacher}
                              isUpdatingStatus={
                                updateInstructionStatusMutation.isPending &&
                                updateInstructionStatusMutation.variables
                                  ?.id === instruction.id
                              }
                              isReordering={reorderLecturesMutation.isPending}
                              isCopying={
                                copyInstructionMutation.isPending &&
                                copyInstructionMutation.variables?.id ===
                                  instruction.id
                              }
                              canMoveUp={lectureIndex > 0}
                              canMoveDown={
                                lectureIndex >= 0 &&
                                lectureIndex < categoryLectures.length - 1
                              }
                              onMoveUp={() =>
                                moveLecture(instruction.id, category.id, -1)
                              }
                              onMoveDown={() =>
                                moveLecture(instruction.id, category.id, 1)
                              }
                              onEdit={(target) =>
                                setLocation(
                                  `${isTeacher ? "/teacher" : "/admin"}/instruction-builder?courseCategory=${category.id}&conditionId=${target.condition_id ?? ""}&instructionId=${target.id}`,
                                )
                              }
                              onCopy={(target) =>
                                copyInstructionMutation.mutate(target)
                              }
                              onToggleStatus={(target) =>
                                updateInstructionStatusMutation.mutate({
                                  id: target.id,
                                  status:
                                    target.review_status === "approved"
                                      ? "in_review"
                                      : "approved",
                                })
                              }
                              onDelete={setDeleteTarget}
                            />
                          );
                        })
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      setLocation(
                        `${isTeacher ? "/teacher" : "/admin"}/instruction-builder?courseCategory=${category.id}`,
                      )
                    }
                    style={{
                      alignSelf: "flex-end",
                      minHeight: 38,
                      marginTop: "auto",
                      padding: "6px 12px",
                      border: `1px solid ${COLORS.line}`,
                      borderRadius: 8,
                      background: COLORS.surface,
                      color: COLORS.ink,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {isReady ? "課題文を追加" : "課題文を作る"}
                    <ArrowRight size={15} />
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleteInstructionMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>課題文を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              課題文 No.{deleteTarget?.id ?? ""}
              「{instructionSummary(deleteTarget)}」を削除します。この操作は元に戻せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteInstructionMutation.isPending}>
              キャンセル
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteInstructionMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (deleteTarget) {
                  deleteInstructionMutation.mutate(deleteTarget.id);
                }
              }}
              style={{ background: COLORS.rose, color: COLORS.surface }}
            >
              {deleteInstructionMutation.isPending && (
                <Loader2 className="mr-2 animate-spin" size={15} />
              )}
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function instructionSummary(instruction: CourseInstruction | null): string {
  if (!instruction) return "";
  return (
    instruction.resolved?.summary?.trim() ||
    instruction.instruction_data?.summary?.trim() ||
    "概要未設定"
  );
}

function InstructionManagementRow({
  instruction,
  lectureNumber,
  isTeacher,
  isUpdatingStatus,
  isReordering,
  isCopying,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onEdit,
  onCopy,
  onToggleStatus,
  onDelete,
}: {
  instruction: CourseInstruction;
  lectureNumber?: number;
  isTeacher: boolean;
  isUpdatingStatus: boolean;
  isReordering: boolean;
  isCopying: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: (instruction: CourseInstruction) => void;
  onCopy: (instruction: CourseInstruction) => void;
  onToggleStatus: (instruction: CourseInstruction) => void;
  onDelete: (instruction: CourseInstruction) => void;
}) {
  const isApproved = instruction.review_status === "approved";
  const selectedGuidanceLevel =
    instruction.instruction_data?.selected_guidance_level;
  return (
    <div
      style={{
        display: "grid",
        gap: 10,
        padding: "14px 4px",
        borderBottom: `1px solid ${COLORS.line}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                background: lectureNumber ? COLORS.cyanSoft : COLORS.surface,
                color: lectureNumber ? COLORS.cyan : COLORS.muted,
                fontSize: 10.5,
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {lectureNumber ? `講座${lectureNumber}` : "講座番号 未割当"}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
              No.{instruction.id}
            </span>
            <span
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                background: isApproved ? COLORS.greenSoft : COLORS.amberSoft,
                color: isApproved ? COLORS.green : COLORS.amber,
                fontSize: 10.5,
                fontWeight: 800,
              }}
            >
              {isApproved
                ? "承認済み"
                : instruction.instruction_data?.superseded_by_instruction_id
                  ? "差し替え済み"
                  : instruction.review_status === "draft"
                    ? "編集中"
                    : "管理者確認待ち"}
            </span>
            {instruction.instruction_data?.source_instruction_id && (
              <span
                style={{
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: COLORS.cyanSoft,
                  color: COLORS.cyan,
                  fontSize: 10.5,
                  fontWeight: 800,
                }}
              >
                公開版 No.{instruction.instruction_data.source_instruction_id} からコピー
              </span>
            )}
            {selectedGuidanceLevel && (
              <span
                style={{
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: COLORS.cyanSoft,
                  color: COLORS.cyan,
                  fontSize: 10.5,
                  fontWeight: 800,
                }}
              >
                採用：{GUIDANCE_LABELS[selectedGuidanceLevel]}
              </span>
            )}
          </div>
          {instruction.instruction_data?.lecture_title?.trim() && (
            <div
              style={{
                marginTop: 7,
                color: COLORS.ink,
                fontSize: 14,
                fontWeight: 900,
                lineHeight: 1.5,
                overflowWrap: "anywhere",
              }}
            >
              {instruction.instruction_data.lecture_title.trim()}
            </div>
          )}
          <div
            style={{
              marginTop: 7,
              color: COLORS.ink,
              fontSize: 12.5,
              fontWeight: 700,
              lineHeight: 1.6,
              overflowWrap: "anywhere",
            }}
          >
            {instructionSummary(instruction)}
          </div>
          <div style={{ marginTop: 4, color: COLORS.muted, fontSize: 10.5, fontVariantNumeric: "tabular-nums" }}>
            健康心理条件 ID {instruction.condition_id ?? "-"}
            {instruction.updated
              ? `・更新 ${new Date(instruction.updated).toLocaleDateString("ja-JP")}`
              : ""}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {isApproved && !isTeacher && (
          <>
            <ManagementButton
              label="上へ"
              icon={<ArrowUp size={13} />}
              disabled={!canMoveUp || isReordering}
              onClick={onMoveUp}
            />
            <ManagementButton
              label="下へ"
              icon={<ArrowDown size={13} />}
              disabled={!canMoveDown || isReordering}
              onClick={onMoveDown}
            />
          </>
        )}
        {isTeacher && isApproved ? (
          <ManagementButton
            label="コピーして編集"
            icon={<Copy size={13} />}
            loading={isCopying}
            onClick={() => onCopy(instruction)}
          />
        ) : (
          <ManagementButton
            label="編集"
            icon={<Pencil size={13} />}
            onClick={() => onEdit(instruction)}
          />
        )}
        {!isTeacher && (
          <ManagementButton
            label={isApproved ? "確認中に戻す" : "承認する"}
            loading={isUpdatingStatus}
            onClick={() => onToggleStatus(instruction)}
          />
        )}
        {!isTeacher && (
          <ManagementButton
            label="削除"
            icon={<Trash2 size={13} />}
            destructive
            onClick={() => onDelete(instruction)}
          />
        )}
      </div>
    </div>
  );
}

function ManagementButton({
  label,
  icon,
  loading = false,
  disabled = false,
  destructive = false,
  onClick,
}: {
  label: string;
  icon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={loading || disabled}
      onClick={onClick}
      style={{
        minHeight: 32,
        padding: "5px 9px",
        border: `1px solid ${destructive ? COLORS.rose : COLORS.line}`,
        borderRadius: 8,
        background: destructive ? COLORS.roseSoft : COLORS.surface,
        color: destructive ? COLORS.rose : COLORS.ink,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "inherit",
        fontSize: 11.5,
        fontWeight: 700,
        cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {loading ? <Loader2 className="animate-spin" size={13} /> : icon}
      {label}
    </button>
  );
}
